import type { AdminPermissions, UserModules } from '@/api/types';
import type { PermissionKey } from '@/api/recognition';
import type { UserRole } from '@/hooks/useCurrentUser';

/**
 * Secciones del panel de administración.
 *
 * 2026-08-08: se sumaron al grupo **Admin General** los catálogos maestros que antes eran
 * subpestañas de Templates (`catalogoFundamentales`, `catalogoOus`) y al grupo **Pulso** la pantalla
 * propia de permisos de Pulso (`rolesPulso`). La gestión de admins globales NO es sección del panel:
 * sigue siendo la vista lateral `users` (`GestionAdministradores`).
 */
export type AdminSection =
  | 'surveys' | 'templates' | 'analytics' | 'rules' | 'notifications'
  | 'system' | 'monitor' | 'trace' | 'jobs'
  | 'catalogoFundamentales' | 'catalogoOus' | 'rolesPulso';

/**
 * Secciones del bloque "Fundamentales" del panel — la administración del Programa de
 * Reconocimiento. Los slugs son los de `views/recognition/admin/sections.tsx`, que es también lo
 * que acepta la ruta `/app/admin/:section`.
 */
export type RecognitionAdminSection =
  | 'roles' | 'puntos' | 'moderacion' | 'ganadores' | 'campanas'
  | 'reporteria' | 'gestion' | 'notificaciones' | 'vistas' | 'documentacion' | 'recap';

/** Módulos del producto. Se combinan con el rol para decidir qué menús ve un usuario. */
export type ProductModule = 'pulso' | 'brillamos';

/**
 * Reglas de acceso por rol. Cada rol declara a qué secciones entra directamente y, en el caso
 * de los administradores de OU, qué permiso granular desbloquea cada sección.
 *
 * Un rol que no aparece aquí no entra a ninguna sección.
 */
const DIRECT_ACCESS: Partial<Record<UserRole, readonly AdminSection[]>> = {
  AdminGlobal: [
    'catalogoFundamentales', 'catalogoOus',
    'surveys', 'templates', 'analytics', 'rules', 'notifications', 'rolesPulso',
    'system', 'monitor', 'trace', 'jobs',
  ],
  // AdminPulso administra Pulso: además de sus secciones operativas, gestiona los permisos de Pulso
  // (`rolesPulso`). La gestión de admins globales vive en la vista lateral `users`, no aquí.
  AdminPulso: ['surveys', 'templates', 'analytics', 'rules', 'notifications', 'rolesPulso'],
  // Brillamos no tiene secciones propias del panel de Pulso hasta la Fase 2 (los catálogos se
  // resuelven aparte, por permiso granular — ver CATALOG_SECTION_PERMISSION).
  AdminBrillamos: [],
};

/**
 * Catálogos maestros del sistema (grupo Admin General). Son transversales: se otorgan de forma
 * granular a administradores de Pulso (flag heredado en `AdminPermissions`) y de Brillamos (clave
 * canónica en el conjunto de permisos de Reconocimiento). `AdminGlobal` siempre los ve (los tiene en
 * `DIRECT_ACCESS`). Se comprueban en `canAccessSection` consultando AMBOS modelos.
 */
const CATALOG_SECTION_PERMISSION: Partial<Record<AdminSection,
  { pulso: keyof AdminPermissions; recognition: PermissionKey }>> = {
  catalogoFundamentales: { pulso: 'canAccessCatalogFundamentals', recognition: 'accessCatalogFundamentals' },
  catalogoOus:           { pulso: 'canAccessCatalogOus',          recognition: 'accessCatalogOus' },
};

/**
 * Secciones que un administrador de OU desbloquea con sus permisos granulares. Los permisos son
 * específicos del módulo Pulso; Admin OU Brillamos no desbloquea nada hasta la Fase 2.
 */
const PERMISSION_UNLOCKED: Partial<Record<UserRole,
  Partial<Record<AdminSection, readonly (keyof AdminPermissions)[]>>>> = {
  AdminOU: {
    surveys: ['canCreateSurveys', 'canApproveSurveys'],
    analytics: ['canViewAnalytics'],
    templates: ['canCreateSurveys'],
  },
};

/**
 * Reglas de acceso al bloque "Fundamentales". Misma forma que `DIRECT_ACCESS`, tabla aparte:
 * son secciones del Programa de Reconocimiento, no del panel de Pulso, y sus roles son los del
 * catálogo aprobado el 30-jul-2026 (`RECOGNITION_ADMIN_ROLES` en `contexts/UserContext.tsx`).
 *
 * `AdminOUBrillamos` administra UNA unidad organizacional, así que se queda con lo operativo y no
 * ve la configuración que es del sistema entero: Roles y Permisos, Puntos, Gestión, Vistas y
 * Documentación. El backend revalida y acota por OU; esto solo evita ofrecer lo que va a rechazar.
 *
 * `AdminOU` NO aparece: en el catálogo nuevo es "Admin OU **Pulso**" (ver la nota de
 * `RECOGNITION_ADMIN_ROLES`).
 */
const RECOGNITION_DIRECT_ACCESS: Partial<Record<UserRole, readonly RecognitionAdminSection[]>> = {
  AdminGlobal: [
    'roles', 'puntos', 'moderacion', 'ganadores', 'campanas',
    'reporteria', 'gestion', 'notificaciones', 'vistas', 'documentacion', 'recap',
  ],
  AdminBrillamos: [
    'roles', 'puntos', 'moderacion', 'ganadores', 'campanas',
    'reporteria', 'gestion', 'notificaciones', 'vistas', 'documentacion', 'recap',
  ],
  AdminOUBrillamos: [
    'moderacion', 'ganadores', 'campanas', 'reporteria', 'notificaciones', 'documentacion',
  ],
};

/** Permiso que gatea cada sección del Admin de Fundamentales — espejo de los comentarios de
 *  RolePermissionMatrix.AdminReconocimientoSet en SharedContracts. */
const RECOGNITION_SECTION_PERMISSION: Record<RecognitionAdminSection, PermissionKey> = {
  roles: 'manageRoles',
  puntos: 'managePointsConfig',
  moderacion: 'moderateContent',
  ganadores: 'manageWinners',
  campanas: 'createCampaigns',
  reporteria: 'viewReports',
  gestion: 'managePoints',
  notificaciones: 'manageNotifications',
  vistas: 'configureViews',
  documentacion: 'manageDocuments',
  recap: 'manageRecap',
};

/**
 * True si el permiso efectivo del usuario (o, en su defecto, su rol) le da acceso a esa sección
 * de Fundamentales.
 *
 * Con `permissions` resuelto y NO VACÍO manda el permiso granular real, editable por administrador
 * (pantalla de Roles y Permisos) — un `AdminOUBrillamos` con "ganadores" activado a mano la ve, y
 * uno al que se le quitó ese permiso deja de verla.
 *
 * Se cae al default de rol (`RECOGNITION_DIRECT_ACCESS`) en dos casos: perfil aún no resuelto
 * (`permissions === null`, p.ej. primer render) y `permissions` resuelto pero VACÍO — una cuenta
 * `AdminBrillamos`/`AdminOUBrillamos` cuyo rol se otorgó por una vía que nunca aplicó la plantilla
 * de permisos (`AppUser.RecognitionPermissions` nace en `[]` en el backend, no en `null` — ver
 * `docs/contexto/errores-conocidos.md`). Sin este respaldo, esa cuenta se queda con CERO permisos
 * y el menú "Administración" desaparece por completo, sin que el propio admin pueda entrar a
 * arreglarlo. Falla hacia accesible (el default de su rol), no hacia bloqueado.
 */
export function canAccessRecognitionAdminSection(
  section: RecognitionAdminSection,
  roles: readonly UserRole[],
  permissions: readonly string[] | null,
): boolean {
  // AdminGlobal siempre ve las 12: es el único rol sin permisos configurables desde esta pantalla.
  if (roles.includes('AdminGlobal')) return true;

  if (permissions !== null && permissions.length > 0) {
    return permissions.includes(RECOGNITION_SECTION_PERMISSION[section]);
  }

  // Respaldo (permissions === null o vacío): el default de nacimiento del rol.
  return roles.some(role => RECOGNITION_DIRECT_ACCESS[role]?.includes(section) ?? false);
}

/**
 * Secciones de Fundamentales accesibles, en el orden en que se declaran en `order`.
 *
 * **El módulo Brillamos no se comprueba aquí, a propósito.** Es la misma decisión que ya rige el
 * bloque "RECONOCIMIENTOS" del menú lateral (`docs/contexto/decisiones.md`, 2026-07-31): el
 * backend devuelve `canViewBrillamos: false` por omisión (`DEFAULT_MODULES` en `api/client.ts`),
 * así que filtrar por módulo escondería la administración del programa a los administradores que
 * de verdad la necesitan. Si algún día el backend puebla ese flag, este es el punto a cambiar.
 */
export function getAccessibleRecognitionAdminSections(
  order: readonly RecognitionAdminSection[],
  roles: readonly UserRole[],
  permissions: readonly string[] | null,
): RecognitionAdminSection[] {
  return order.filter(section => canAccessRecognitionAdminSection(section, roles, permissions));
}

/**
 * True si alguno de los roles del usuario le da acceso a la sección.
 *
 * Los catálogos maestros (`CATALOG_SECTION_PERMISSION`) son transversales: se conceden por permiso
 * granular a admins de Pulso (flag en `permissions`) o de Brillamos (clave en `recognitionPermissions`),
 * además del `AdminGlobal` que los tiene por `DIRECT_ACCESS`. `recognitionPermissions` es opcional
 * (null mientras el perfil no resuelve); el resto de secciones lo ignora.
 */
export function canAccessSection(
  section: AdminSection,
  roles: readonly UserRole[],
  permissions: AdminPermissions,
  recognitionPermissions: readonly string[] | null = null,
): boolean {
  const catalog = CATALOG_SECTION_PERMISSION[section];
  if (catalog) {
    if (roles.includes('AdminGlobal')) return true;
    if (permissions[catalog.pulso]) return true;
    return recognitionPermissions?.includes(catalog.recognition) ?? false;
  }
  return roles.some(role => {
    if (DIRECT_ACCESS[role]?.includes(section)) return true;
    const unlocking = PERMISSION_UNLOCKED[role]?.[section];
    return unlocking?.some(permission => permissions[permission]) ?? false;
  });
}

/** Secciones accesibles, en el orden en que se declaran en `order`. */
export function getAccessibleSections(
  order: readonly AdminSection[],
  roles: readonly UserRole[],
  permissions: AdminPermissions,
  recognitionPermissions: readonly string[] | null = null,
): AdminSection[] {
  return order.filter(section => canAccessSection(section, roles, permissions, recognitionPermissions));
}

/** True si el usuario tiene habilitado ese módulo del producto. */
export function canAccessModule(productModule: ProductModule, modules: UserModules): boolean {
  return productModule === 'pulso' ? modules.canViewPulso : modules.canViewBrillamos;
}
